import { useEffect, useState } from 'react';
import { adminApi, downloadBlob } from '@/lib/api';
import { Plus, Edit, Loader2, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [form, setForm] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    gst_no: '',
    is_active: true
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getVendors();
      setVendors(response.data || []);
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      gst_no: '',
      is_active: true
    });
    setSelectedVendor(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Please enter vendor name');
      return;
    }
    setSaving(true);
    try {
      if (selectedVendor) {
        await adminApi.updateVendor(selectedVendor.id, form);
        toast.success('Vendor updated');
      } else {
        await adminApi.createVendor(form);
        toast.success('Vendor created');
      }
      setShowModal(false);
      resetForm();
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (vendor) => {
    setSelectedVendor(vendor);
    setForm({
      name: vendor.name || '',
      contact_person: vendor.contact_person || '',
      phone: vendor.phone || '',
      email: vendor.email || '',
      address: vendor.address || '',
      gst_no: vendor.gst_no || '',
      is_active: vendor.is_active
    });
    setShowModal(true);
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const res = await adminApi.downloadAdminDatasetExcel('vendors');
      downloadBlob(res.data, 'vendors.xlsx');
    } catch (error) {
      toast.error('Failed to download Excel');
    } finally {
      setDownloadingExcel(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="vendors-page">
      <LoadingOverlay open={downloadingExcel} message="Please wait. Report is being generated" />
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Vendors</h1>
        <p className="text-[#A8A8A8] mt-1">Manage vendor profiles</p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#2D2420]">Vendor List</h2>
              <p className="text-sm text-[#A8A8A8]">Add and edit vendors</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleDownloadExcel}>
                Download Excel
              </Button>
              <Button onClick={() => { resetForm(); setShowModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Vendor
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
            </div>
          ) : vendors.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-[#A8A8A8]">
              <Truck className="w-6 h-6 mr-2" />
              No vendors yet
            </div>
          ) : (
            <div className="overflow-x-auto mt-4">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Contact</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Phone</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {vendors.map((vendor) => (
                    <tr key={vendor.id} className="hover:bg-[#FAFAF9]/60">
                      <td className="py-3 px-4 text-[#2D2420] font-medium">{vendor.name}</td>
                      <td className="py-3 px-4 text-[#A8A8A8]">{vendor.contact_person || '-'}</td>
                      <td className="py-3 px-4 text-[#A8A8A8]">{vendor.phone || '-'}</td>
                      <td className="py-3 px-4">
                        <Badge className={cn("text-xs", vendor.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800")}>
                          {vendor.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(vendor)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">{selectedVendor ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Contact Person</Label>
                <Input value={form.contact_person} onChange={(e) => setForm((prev) => ({ ...prev, contact_person: e.target.value }))} className="border-[#E5E5E5]" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} className="border-[#E5E5E5]" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} className="border-[#E5E5E5]" />
              </div>
              <div>
                <Label>GST No</Label>
                <Input value={form.gst_no} onChange={(e) => setForm((prev) => ({ ...prev, gst_no: e.target.value }))} className="border-[#E5E5E5]" />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Textarea value={form.address} onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))} className="border-[#E5E5E5]" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={form.is_active} onCheckedChange={(value) => setForm((prev) => ({ ...prev, is_active: value }))} />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
